// FILE: src/components/Admin/AI/kb/PipelineCard.tsx
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import ManagedUploadStatusPanel from './ManagedUploadStatusPanel';

type Props = {
  pipelineId: string;
  pipeline?: any;
  provider?: string;
  loading?: boolean;
  taskId?: string | null;
  executionId?: string | null;
  uploadStatus?: any;
  ingestionStatus?: any;
  managedUploadStatus?: any;
  onRefresh: () => void;
  onAddFiles: () => void;
  disableUpload?: boolean;
};

const PipelineCard: React.FC<Props> = ({
                                         pipelineId,
                                         pipeline,
                                         provider = 'drift',
                                         loading,
                                         taskId,
                                         executionId,
                                         uploadStatus,
                                         ingestionStatus,
                                         managedUploadStatus,
                                         onRefresh,
                                         onAddFiles,
                                         disableUpload,
                                       }) => {
  const uploadText = uploadStatus?.status || uploadStatus?.current_status || 'idle';
  const ingestionText =
    ingestionStatus?.current_status || ingestionStatus?.status || 'idle';

  const isDrift = provider === 'drift';

  return (
    <Stack spacing={2}>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h6">Selected Pipeline Knowledge Base</Typography>
            <Typography variant="body2" color="text.secondary">
              View pipeline metadata, upload documents, and monitor ingestion for the selected RAG pipeline.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={onAddFiles}
              disabled={disableUpload || !isDrift || !pipelineId || !!loading}
            >
              Add Files
            </Button>
          </Stack>
        </Stack>

        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {!isDrift ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            File metadata and direct upload controls are currently available for DRIFT pipelines.
            RAGaaS pipelines use GCS/PubSub ingestion configuration and secrets.
          </Alert>
        ) : null}

        <Stack spacing={1.25}>
          <Chip variant="outlined" label={`Provider: ${provider || '-'}`} />
          <Chip variant="outlined" label={`Pipeline: ${pipelineId || 'backend default'}`} />
          <Chip variant="outlined" label={`Upload Task: ${taskId || '-'}`} />
          <Chip variant="outlined" label={`Ingestion Execution: ${executionId || '-'}`} />

          {pipeline?.name || pipeline?.status ? (
            <Typography variant="body2">
              Pipeline Status: <b>{String(pipeline?.status || 'unknown')}</b>
              {pipeline?.name ? ` / ${pipeline.name}` : ''}
            </Typography>
          ) : null}

          <Typography variant="body2">
            Upload Status: <b>{String(uploadText)}</b>
          </Typography>

          <Typography variant="body2">
            Ingestion Status: <b>{String(ingestionText)}</b>
            {ingestionStatus?.current_stage ? ` (${ingestionStatus.current_stage})` : ''}
          </Typography>

          {uploadStatus?.error ? (
            <Typography variant="body2" color="error">
              Upload Error: {String(uploadStatus.error)}
            </Typography>
          ) : null}

          {ingestionStatus?.error ? (
            <Typography variant="body2" color="error">
              Ingestion Error: {String(ingestionStatus.error)}
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      <ManagedUploadStatusPanel
        status={managedUploadStatus}
        busy={loading}
      />
    </Stack>
  );
};

export default PipelineCard;